import clsx from "clsx";
import { _, _any } from "i18n";
import type { Keys } from "i18n/locales/types";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { Badge, ButtonTabs, ButtonTabsItem } from "ui-kit";
import clubPlaceholder from "ui-kit/assets/images/logo_fixtures.png";
import type { LinedTabsItem } from "ui-kit/src/LinedTabs/LinedTabs";
import Loader from "ui-kit/src/Loader/Loader";
import Modal from "ui-kit/src/Modal";
import { useStatsPeriodSelection } from "../../hooks/useStatsPeriodSelection";
import { useGameStore } from "../../store";
import {
	EventFilterValue,
	GPGamePlayerResponse,
	GPGameTeamResponse,
	GamePageLazyResponse,
	GamePageResponse,
	GamePlayerStatResponse,
	GameStatsExportCellRequest,
	GameStatsExportRequest,
	NavOptions,
	ProcessingLevelEnum,
	VideoSidebar,
} from "../../types";
import { getTaggedGameTeam, getTaggedTeamSide, isOneTeamTagged } from "../../utils/teamTagged";
import OneTeamTaggingDisclaimer from "../OneTeamTaggingDisclaimer";
import { StatsLinedTabsAdapter } from "../StatsLinedTabsAdapter";
import "./PlayerStats.css";
import { PlayerStatsExport } from "./PlayerStatsExport";
import { TableCell } from "./TableCell";
import {
	PlayerStatCalculationResult,
	PlayerStatCategory,
	PlayerStatType,
	getPlayerStatDefinitionByCategory,
	hintedColumns,
	precomputedGoalkeeperStatTypes,
	showEnhancedGoalkeeperStats,
} from "./definition";
import { calculatePlayerStats } from "./functions";
import { calculatePeriodFilteredGoalkeeperStat } from "./periodFilteredGoalkeeperStats";
import { formatPrecomputedGoalkeeperStat, getPrecomputedStatsLookup, isFullMatchPeriodSelection } from "./precomputedGoalkeeperStats";

interface PlayerStatsProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	gamePlayerStat?: GamePlayerStatResponse | null;
	gamePlayerStatIsLoading?: boolean;
	currentNavOption: NavOptions;
	tabs: LinedTabsItem[];
	handleChangeNavOptions: (tab: NavOptions) => void;
	handleGameStatsExport: (tableExportRequest: GameStatsExportRequest) => void;
}

export function PlayerStats({
	gameData,
	gameDataLazy,
	gamePlayerStat,
	gamePlayerStatIsLoading = false,
	currentNavOption,
	tabs,
	handleChangeNavOptions,
	handleGameStatsExport,
}: PlayerStatsProps) {
	const taggedGameTeam = getTaggedGameTeam(gameData);
	const taggedSide = getTaggedTeamSide(gameData.game?.teamTagged);
	const isOtt = isOneTeamTagged(gameData.game?.teamTagged);

	const [sortColumn, setSortColumn] = useState<PlayerStatType>(PlayerStatType.Number);
	const [sortDirection, setSortDirection] = useState<string>("ASC");
	const [category, setCategory] = useState<PlayerStatCategory>(PlayerStatCategory.Summary);
	const [gameTeam, setGameTeam] = useState<number>(taggedGameTeam?.id ?? gameData.homeGameTeam.id);
	const isLightTagged = gameData?.game?.processingLevel === ProcessingLevelEnum.Limited;

	const { setFilterValues, setFilteredFilterValues, setSidebar } = useGameStore();
	const { eventFilter, periodTabs, periods, availablePeriodKeys, isEventFilterHydrated, handlePeriodsChange } =
		useStatsPeriodSelection(gameData.game.id);

	const [exportModal, setExportModal] = useState<boolean>(false);

	const handleOnCategoryChange = (keys: (string | number)[]) => {
		setCategory(keys[0] as PlayerStatCategory);
		setSortColumn(PlayerStatType.Number);
		setSortDirection("ASC");
	};

	const handleOnTeamChange = (keys: (string | number)[]) => {
		setGameTeam(keys[0] as number);
	};

	const handleOnCellClick = (result: PlayerStatCalculationResult) => {
		if (!eventFilter || result.filterValues.length === 0) return;
		let filterValues: EventFilterValue[] = eventFilter.mutateFilterValuesByPlayerId([], result.playerId);

		result.filterValues.forEach((fv) => {
			fv.value.forEach((val) => {
				filterValues = eventFilter.mutateFilterValues(filterValues, fv.key, val);
			});
		});

		setFilteredFilterValues(filterValues);
		setFilterValues(filterValues);
		handleChangeNavOptions(NavOptions.FullGame);
		setSidebar(VideoSidebar.EventList);
	};

	const handleOnSort = (newSortColumn: PlayerStatType) => {
		if (newSortColumn === sortColumn) {
			setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
		} else {
			setSortColumn(newSortColumn);

			if ([PlayerStatType.Name, PlayerStatType.Number].includes(newSortColumn)) {
				setSortDirection("ASC");
			} else {
				setSortDirection("DESC");
			}
		}
	};

	const processingLevel = gameData?.game?.processingLevel as ProcessingLevelEnum | undefined;

	const precomputedStatsLookup = useMemo(() => getPrecomputedStatsLookup(gamePlayerStat?.players ?? []), [gamePlayerStat?.players]);

	const calculateStatCell = useCallback(
		(statType: PlayerStatType, player: GPGamePlayerResponse, statPeriods: string[] = periods): PlayerStatCalculationResult => {
			if (precomputedGoalkeeperStatTypes.has(statType) && isFullMatchPeriodSelection(statPeriods, availablePeriodKeys)) {
				return formatPrecomputedGoalkeeperStat(statType, player, precomputedStatsLookup);
			}

			if (eventFilter === null || eventFilter === undefined) {
				return {
					url: player.playerUrl,
					playerId: player.id!,
					statType,
					label: "",
					value: null,
					valueFormatted: null,
					valueSort: 0,
					events: [],
					filterValues: [],
					isFilterable: false,
					isUseful: false,
				};
			}

			if (precomputedGoalkeeperStatTypes.has(statType)) {
				return calculatePeriodFilteredGoalkeeperStat(statType, player, statPeriods, eventFilter.allEvents);
			}

			return calculatePlayerStats(eventFilter, statType, player, statPeriods, processingLevel ?? null);
		},
		[availablePeriodKeys, eventFilter, periods, precomputedStatsLookup, processingLevel]
	);

	const allPlayers = useMemo(
		() => [...gameData.homeGameTeam.players, ...gameData.awayGameTeam.players],
		[gameData.awayGameTeam.players, gameData.homeGameTeam.players]
	);

	const handleOnPlayerStatsExport = (
		format: "csv" | "xlsx",
		home: boolean,
		away: boolean,
		categories: PlayerStatCategory[],
		exportPeriods: string[]
	) => {
		if (!eventFilter) return;
		const request = {
			type: "playerStats",
			name: `game-${gameData.game.id}-player-stats`,
			format: format,
			header: [
				{
					key: "team",
					value: _(`gamePage.playerStats.export.column.team`),
				},
			],
			rows: [],
		} as GameStatsExportRequest;

		const usedTypes: PlayerStatType[] = [];
		const processingLevel = gameData?.game?.processingLevel as ProcessingLevelEnum | undefined;
		const providerTag = gameData?.game?.providerTag ?? null;
		const teamTagged = gameData?.game?.teamTagged ?? null;

		categories.forEach((c) => {
			getPlayerStatDefinitionByCategory(c, gameData.permissions.showRatings, processingLevel, providerTag, teamTagged).forEach((t) => {
				if (!usedTypes.includes(t)) {
					request.header.push({
						key: t.toString(),
						value: _any(`gamePage.playerStats.type.${t}`),
					});
					usedTypes.push(t);
				}
			});
		});

		const exportHome = isOtt ? taggedSide === "home" : home;
		const exportAway = isOtt ? taggedSide === "away" : away;

		const teams: GPGameTeamResponse[] = [];

		if (exportHome) {
			teams.push(gameData.homeGameTeam);
		}

		if (exportAway) {
			teams.push(gameData.awayGameTeam);
		}

		teams.forEach((gt) => {
			const pl = allPlayers
				.filter((p) => {
					if (gameData.permissions.allowedPlayers !== null && p.id !== null && !gameData.permissions.allowedPlayers.includes(p.id)) {
						return false;
					}

					return true;
				})
				.filter((p) => {
					const isFromTeam = p.teamId === gt.id;
					const isOnlyGoalkeeper = categories.length === 1 && categories[0] === PlayerStatCategory.Goalkeeper;
					const containsGoalkeeper = categories.includes(PlayerStatCategory.Goalkeeper);

					if (isOnlyGoalkeeper) {
						return isFromTeam && p.position === "G";
					}
					if (isOnlyGoalkeeper) {
						return isFromTeam && p.position === "G";
					}

					if (!containsGoalkeeper) {
						return isFromTeam && p.position !== "G";
					}
					if (!containsGoalkeeper) {
						return isFromTeam && p.position !== "G";
					}

					return isFromTeam;
				});

			pl.forEach((gp) => {
				const row: GameStatsExportCellRequest[] = [
					{
						key: "team",
						value: gt.clubName,
					},
				] as GameStatsExportCellRequest[];
				usedTypes.forEach((t) => {
					const result = calculateStatCell(t, gp, exportPeriods);
					row.push({
						key: t,
						value: result.valueFormatted?.toString() || "",
					});
				});
				(request.rows as unknown as GameStatsExportCellRequest[][]).push(row);
			});
		});

		handleGameStatsExport(request);
		setExportModal(false);
	};
	const clubTabs: ButtonTabsItem[] = [];

	if (isOtt === false || taggedSide === "home") {
		clubTabs.push({
			key: gameData.homeGameTeam.id,
			label: gameData.homeGameTeam.clubName || "Home Team",
			image: gameData.homeGameTeam.logo || clubPlaceholder,
			imageShape: "round",
		});
	}

	if (isOtt === false || taggedSide === "away") {
		clubTabs.push({
			key: gameData.awayGameTeam.id,
			label: gameData.awayGameTeam.clubName || "Away Team",
			image: gameData.awayGameTeam.logo || clubPlaceholder,
			imageShape: "round",
		});
	}

	const categories = Object.values(PlayerStatCategory).map((cat) => ({
		key: cat,
		label: _(`game.stats.menu.${cat}` as keyof Keys),
	}));

	const processingLevelForColumns = gameData?.game?.processingLevel as ProcessingLevelEnum | undefined;
	const providerTagForColumns = gameData?.game?.providerTag ?? null;
	const teamTaggedForColumns = gameData?.game?.teamTagged ?? null;
	const columns = useMemo(
		() =>
			getPlayerStatDefinitionByCategory(
				category,
				gameData.permissions.showRatings,
				processingLevelForColumns,
				providerTagForColumns,
				teamTaggedForColumns
			),
		[category, gameData.permissions.showRatings, processingLevelForColumns, providerTagForColumns, teamTaggedForColumns]
	);

	const allowedPlayerIds = gameData.permissions.allowedPlayers;

	const isTableLoading =
		category === PlayerStatCategory.Goalkeeper && showEnhancedGoalkeeperStats
			? gamePlayerStatIsLoading || isEventFilterHydrated === false
			: isEventFilterHydrated === false;

	const displayData = useMemo(() => {
		if (isEventFilterHydrated === false) {
			return [];
		}

		const rows: PlayerStatCalculationResult[][] = [];
		allPlayers.forEach((player: GPGamePlayerResponse) => {
			const isOnSelectedTeam = player.teamId === gameTeam;
			const isGoalkeeperRow = category !== PlayerStatCategory.Goalkeeper || player.position === "G";
			if (isOnSelectedTeam === false || isGoalkeeperRow === false) {
				return;
			}

			if (allowedPlayerIds !== null && player.id !== null && allowedPlayerIds.includes(player.id) === false) {
				return;
			}

			const row: PlayerStatCalculationResult[] = [];
			columns.forEach((statType: PlayerStatType) => {
				row.push(calculateStatCell(statType, player));
			});
			rows.push(row);
		});

		rows.sort((row1: PlayerStatCalculationResult[], row2: PlayerStatCalculationResult[]) => {
			const compare1 = row1.find((cell) => cell.statType === sortColumn);
			const compare2 = row2.find((cell) => cell.statType === sortColumn);
			if (compare1 === undefined || compare2 === undefined) {
				return 0;
			}

			let result: number;
			if ([PlayerStatType.Name, PlayerStatType.Position].includes(sortColumn)) {
				result = `${compare1.valueSort}` < `${compare2.valueSort}` ? -1 : 1;
			} else {
				result = Number(compare1.valueSort) < Number(compare2.valueSort) ? -1 : 1;
			}

			return result * (sortDirection === "ASC" ? 1 : -1);
		});

		return rows;
	}, [allPlayers, allowedPlayerIds, calculateStatCell, category, columns, gameTeam, isEventFilterHydrated, sortColumn, sortDirection]);

	return (
		<>
			<StatsLinedTabsAdapter tabs={tabs} active={currentNavOption} onChange={handleChangeNavOptions}>
				{gameData.permissions.downloadPlayerStats && (
					<Badge
						label={_(`gamePage.playerStats.export.button`)}
						variant="primary"
						icon="plus-white"
						size="md"
						onClick={() => setExportModal(true)}
					/>
				)}
			</StatsLinedTabsAdapter>

			<div className="stats-container">
				<div className="game-data__nav">
					<ButtonTabs items={categories} active={[category]} showCheck={true} onChange={handleOnCategoryChange} />
					<ButtonTabs items={periodTabs} active={periods} showCheck={true} onChange={handlePeriodsChange} isMultiple={true} />
					{clubTabs.length > 1 && <ButtonTabs items={clubTabs} active={[gameTeam]} onChange={handleOnTeamChange} />}
				</div>
				{isOtt && <OneTeamTaggingDisclaimer />}
				<table className="player-stats-table">
					<thead>
						<tr>
							{columns.map((column, c) => (
								<th
									key={c}
									onClick={() => handleOnSort(column)}
									className={clsx({
										"sort-asc": sortColumn === column && sortDirection === "ASC",
										"sort-desc": sortColumn === column && sortDirection === "DESC",
										"limited-tagged": isLightTagged && c === 0,
									})}
								>
									<div className="player-stats-table__th">
										<span>{_(`gamePage.playerStats.type.${column}` as keyof Keys)}</span>
										{hintedColumns.includes(column) && (
											<>
												<span className="icon-info-outline icon-new icon-white icon-16 info-icon"></span>
												<div className="info-box">
													<div className="info-box-title">{_(`gamePage.playerStats.type.${column}` as keyof Keys)}</div>
													<div className="info-box-info">
														{_(`gamePage.playerStats.description.${column}` as keyof Keys)}
													</div>
												</div>
											</>
										)}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{isTableLoading ? (
							<tr>
								<td colSpan={columns.length} className="player-stats-table__loader">
									<Loader />
								</td>
							</tr>
						) : (
							displayData.map((row: PlayerStatCalculationResult[], rowIndex: number) => (
								<tr key={`row-${rowIndex}`}>
									{row.map((calculation: PlayerStatCalculationResult, cellIndex) => {
										const isClickable = gameData.permissions.showEvents && (calculation?.isFilterable || false);
										const isUseful = calculation?.isUseful || false;
										const isRatio = calculation?.statType.endsWith("Ratio");
										const isPlayerName = calculation?.statType === PlayerStatType.Name && calculation.url !== null;

										return (
											<TableCell
												key={`cell-${cellIndex}`}
												row={rowIndex}
												column={cellIndex}
												className={clsx({
													"has-sort": calculation.statType === sortColumn,
													"has-click": isClickable,
													"align-center": calculation.statType !== PlayerStatType.Name,
												})}
												onClick={isClickable ? () => handleOnCellClick(calculation) : undefined}
											>
												{calculation && (
													<>
														{!isPlayerName && isUseful && <strong>{calculation.valueFormatted}</strong>}
														{!isPlayerName && !isUseful && calculation.valueFormatted}
														{!isPlayerName && isRatio && (
															<span className="cell-slash-percentage">{calculation.value}%</span>
														)}
														{isPlayerName && (
															<Link to={calculation.url as string} target="_blank">
																<strong>{calculation.value}</strong>
															</Link>
														)}
													</>
												)}
											</TableCell>
										);
									})}
								</tr>
							))
						)}
					</tbody>
				</table>
				<Modal title={_(`gamePage.playerStats.export.button`)} show={exportModal} size="xs" onClose={() => setExportModal(false)}>
					<PlayerStatsExport
						gameData={gameData}
						category={category}
						team={gameTeam}
						gamePeriods={periodTabs}
						selectedPeriods={periods}
						onExportPlayerStats={handleOnPlayerStatsExport}
					/>
				</Modal>
			</div>
		</>
	);
}
